import React, { useState, useMemo } from 'react';
import {
  TrendingUp,
  Receipt,
  Award,
  Activity,
  Layers,
  Info,
  AlertTriangle,
  CheckCircle2,
  Users,
  Wheat,
  Scale,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  FileSpreadsheet,
  FileWarning,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TabularNumber } from '@/components/common/TabularNumber';
import { PesoIcon } from '@/components/common/PesoIcon';
import { MetricHelpTooltip } from '@/components/reports/MetricHelpTooltip';
import { ManagementSummaryData, CyclePerformanceData } from '@/services/reportService';
import { formatCurrency, formatNumber } from '@/lib/utils';

interface ManagementSummaryViewProps {
  summary: ManagementSummaryData;
}

type SortField = 'cycleCode' | 'growerName' | 'soldHead' | 'totalRevenue' | 'grossProfit' | 'grossMarginPct';
type SortDirection = 'asc' | 'desc';

export const ManagementSummaryView: React.FC<ManagementSummaryViewProps> = ({ summary }) => {
  // Sort state for Completed Cycle Performance table
  const [sortField, setSortField] = useState<SortField>('grossMarginPct');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showAllCompleted, setShowAllCompleted] = useState<boolean>(false);

  const isAllYears = summary.selectedYear === 'all';
  const yearTitle = isAllYears ? 'All Years (Multi-Year Portfolio)' : `Reporting Year ${summary.selectedYear}`;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedCompletedCycles = useMemo(() => {
    const cycles = [...summary.completedPerformance.completedCycles];
    return cycles.sort((a, b) => {
      let valA: any = a[sortField];
      let valB: any = b[sortField];

      if (valA === null || valA === undefined) valA = sortDirection === 'asc' ? Infinity : -Infinity;
      if (valB === null || valB === undefined) valB = sortDirection === 'asc' ? Infinity : -Infinity;

      if (typeof valA === 'string') {
        return sortDirection === 'asc'
          ? valA.localeCompare(valB as string)
          : (valB as string).localeCompare(valA);
      }

      return sortDirection === 'asc' ? Number(valA) - Number(valB) : Number(valB) - Number(valA);
    });
  }, [summary.completedPerformance.completedCycles, sortField, sortDirection]);

  const visibleCompletedCycles = showAllCompleted
    ? sortedCompletedCycles
    : sortedCompletedCycles.slice(0, 5);

  const costMixHelpMap: Record<string, string> = {
    feed: 'costFeed',
    piglets: 'costPiglets',
    medication: 'costMedication',
    other: 'costOther',
  };

  return (
    <div className="space-y-6">
      {/* View Section Heading */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 pb-1 border-b border-slate-200">
        <div>
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span>Executive Management Summary — {yearTitle}</span>
          </h2>
          <p className="text-xs text-slate-500">
            {isAllYears
              ? 'Multi-year owner dashboard, portfolio profitability, and operational performance.'
              : `Owner performance snapshot, cash movement, and closed cycle economics for ${summary.selectedYear}.`}
          </p>
        </div>
      </div>

      {/* =========================================================
          1. FINANCIAL SNAPSHOT
          ========================================================= */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider text-slate-800">
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-emerald-800 text-white text-[10px] font-bold">1</span>
            <span>Financial Snapshot</span>
          </div>
          <span className="text-[10px] font-mono text-slate-400">Calendar Ledger vs Closed Cohort</span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
          {/* Card 1: Booked Pig Sales */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="bookedPigSales"
                  label="Booked Pig Sales"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <PesoIcon className="w-4 h-4 text-emerald-600 shrink-0" />
              </div>
              <div>
                <div className="text-sm sm:text-lg font-bold font-mono text-slate-900 tracking-tight">
                  {formatCurrency(summary.totalBookedRevenue)}
                </div>
                <div className="flex items-center justify-between mt-0.5 text-[9px] sm:text-[10px] text-slate-500">
                  <span>Account 1010 Booked</span>
                  {summary.priorYear && summary.priorYear.revenueGrowthPct !== null && (
                    <span className={`font-semibold ${summary.priorYear.revenueGrowthPct >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                      vs {summary.priorYear.year}: {summary.priorYear.revenueGrowthPct >= 0 ? '+' : ''}{summary.priorYear.revenueGrowthPct}%
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Booked Production Costs */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="bookedProductionCosts"
                  label="Booked Costs"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <Receipt className="w-4 h-4 text-rose-600 shrink-0" />
              </div>
              <div>
                <div className="text-sm sm:text-lg font-bold font-mono text-slate-900 tracking-tight">
                  {formatCurrency(summary.totalBookedProductionCosts)}
                </div>
                <div className="flex items-center justify-between mt-0.5 text-[9px] sm:text-[10px] text-slate-500">
                  <span>Accounts 3010–3051</span>
                  {summary.priorYear && summary.priorYear.costGrowthPct !== null && (
                    <span className={`font-semibold ${summary.priorYear.costGrowthPct <= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                      vs {summary.priorYear.year}: {summary.priorYear.costGrowthPct >= 0 ? '+' : ''}{summary.priorYear.costGrowthPct}%
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 3: Completed-Cycle Gross Profit */}
          <Card className="border-slate-200 shadow-sm bg-emerald-50/40 hover:border-emerald-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-emerald-900 mb-1">
                <MetricHelpTooltip
                  helpKey="completedCyclesGrossProfit"
                  label="Completed Gross Profit"
                  className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider truncate text-emerald-900"
                  iconClassName="w-3 h-3 text-emerald-700/70 group-hover:text-emerald-800"
                />
                <Award className="w-4 h-4 text-emerald-700 shrink-0" />
              </div>
              <div>
                <div className="text-sm sm:text-lg font-bold font-mono text-emerald-900 tracking-tight">
                  {formatCurrency(summary.completedCyclesGrossProfit)}
                </div>
                <p className="text-[9px] sm:text-[10px] text-emerald-700/80 mt-0.5 truncate">
                  Across {summary.completedPerformance.completedCycles.length} Settled/Closed Cycles
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Card 4: Completed-Cycle Gross Margin % */}
          <Card className="border-slate-200 shadow-sm bg-emerald-50/40 hover:border-emerald-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-emerald-900 mb-1">
                <MetricHelpTooltip
                  helpKey="completedCyclesGrossMarginPct"
                  label="Completed Margin %"
                  className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider truncate text-emerald-900"
                  iconClassName="w-3 h-3 text-emerald-700/70 group-hover:text-emerald-800"
                />
                <TrendingUp className="w-4 h-4 text-emerald-700 shrink-0" />
              </div>
              <div>
                <div className="text-sm sm:text-lg font-bold font-mono text-emerald-900 tracking-tight">
                  {summary.completedCyclesGrossMarginPct !== null ? `${summary.completedCyclesGrossMarginPct}%` : '—'}
                </div>
                <p className="text-[9px] sm:text-[10px] text-emerald-700/80 mt-0.5 truncate">
                  Profit / Revenue (Closed Batch)
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Financial Accounting Footnote */}
        <div className="flex items-start gap-2 p-2 rounded-lg bg-blue-50/60 border border-blue-200/70 text-[11px] text-blue-900">
          <Info className="w-4 h-4 text-blue-700 shrink-0 mt-0.5" />
          <p className="leading-snug">
            <span className="font-bold">Accounting Principle:</span> Booked Pig Sales & Production Costs reflect calendar ledger movements for the period. Completed-Cycle Gross Profit reflects true batch-level profitability of finished cycles.
          </p>
        </div>
      </div>

      {/* =========================================================
          2. CURRENT PRODUCTION
          ========================================================= */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider text-slate-800">
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-slate-800 text-white text-[10px] font-bold">2</span>
            <span>Current Production</span>
          </div>
          <span className="text-[10px] font-mono text-slate-400">Live Operational Herd & Scale Sales</span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
          {/* Card 1: Active Cycles */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="activeCycles"
                  label="Active Cycles"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <Activity className="w-4 h-4 text-emerald-600 shrink-0" />
              </div>
              <div>
                <div className="text-sm sm:text-lg font-bold font-mono text-emerald-800 tracking-tight">
                  {summary.activeCyclesCount}
                </div>
                <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5 truncate">
                  Running in Pens
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Live Pigs on Feed */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="livePigsOnFeed"
                  label="Live Pigs on Feed"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <Users className="w-4 h-4 text-slate-600 shrink-0" />
              </div>
              <div>
                <div className="text-sm sm:text-lg font-bold font-mono text-slate-900 tracking-tight">
                  {formatNumber(summary.livePigsOnFeed)} <span className="text-xs font-normal text-slate-500">head</span>
                </div>
                <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5 truncate">
                  Placed minus Sold & Mortality
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Card 3: Pigs Sold */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="pigsSold"
                  label="Pigs Sold"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <Scale className="w-4 h-4 text-indigo-600 shrink-0" />
              </div>
              <div>
                <div className="text-sm sm:text-lg font-bold font-mono text-indigo-950 tracking-tight">
                  {formatNumber(summary.totalPigsSold)} <span className="text-xs font-normal text-slate-500">head</span>
                </div>
                <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5 truncate">
                  Scale Harvest Volume
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Card 4: Mortality */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="mortality"
                  label="Mortality"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <FileWarning className="w-4 h-4 text-amber-600 shrink-0" />
              </div>
              <div>
                <div className="text-sm sm:text-lg font-bold font-mono text-slate-900 tracking-tight">
                  {formatNumber(summary.totalMortalityHead)} <span className="text-xs font-normal text-slate-500">head</span>
                </div>
                <p className="text-[9px] sm:text-[10px] text-slate-500 mt-0.5 truncate">
                  {summary.hasValidMortalityRate && summary.overallMortalityRatePct !== null
                    ? `Rate: ${summary.overallMortalityRatePct}% of placed herd`
                    : 'Rate: — (no placed denominator)'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* =========================================================
          3. GROWER SETTLEMENT POSITION
          ========================================================= */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider text-slate-800">
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-slate-800 text-white text-[10px] font-bold">3</span>
            <span>Grower Settlement Position</span>
          </div>
          <span className="text-[10px] font-mono text-slate-400">Actual Calculated Obligations</span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
          {/* Finalized */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="finalizedSettlements"
                  label="Finalized"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              </div>
              <div>
                <div className="text-sm sm:text-lg font-bold font-mono text-emerald-800 tracking-tight">
                  {summary.settlementPosition.finalizedCount}
                </div>
                <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5 truncate">
                  Frozen & Paid Settlements
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Approved */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="approvedSettlements"
                  label="Approved"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <Activity className="w-4 h-4 text-indigo-600 shrink-0" />
              </div>
              <div>
                <div className="text-sm sm:text-lg font-bold font-mono text-indigo-900 tracking-tight">
                  {summary.settlementPosition.approvedCount}
                </div>
                <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5 truncate">
                  Awaiting Final Freeze
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Draft */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="draftSettlements"
                  label="Draft"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <Receipt className="w-4 h-4 text-amber-600 shrink-0" />
              </div>
              <div>
                <div className="text-sm sm:text-lg font-bold font-mono text-amber-800 tracking-tight">
                  {summary.settlementPosition.draftCount}
                </div>
                <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5 truncate">
                  In-Progress Calculations
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Amount Outstanding */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="amountOutstanding"
                  label="Amount Outstanding"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <PesoIcon className="w-4 h-4 text-rose-600 shrink-0" />
              </div>
              <div>
                <div className="text-sm sm:text-lg font-bold font-mono text-rose-800 tracking-tight">
                  {formatCurrency(summary.settlementPosition.amountOutstanding)}
                </div>
                <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5 truncate">
                  Calculated Draft + Approved Payouts
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* =========================================================
          4. COMPLETED CYCLE PERFORMANCE & MANAGEMENT TABLE
          ========================================================= */}
      <div className="space-y-3 bg-white p-4 sm:p-5 border border-slate-200 rounded-xl shadow-xs">
        <div className="flex items-center justify-between pb-2 border-b border-slate-100">
          <div className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider text-slate-800">
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-slate-800 text-white text-[10px] font-bold">4</span>
            <span>Completed Cycle Performance</span>
          </div>
          <span className="text-xs font-semibold text-slate-500">
            {summary.completedPerformance.completedCycles.length} Finished Cycles
          </span>
        </div>

        {/* Three Prominent Performance Callouts */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
          {/* Best Margin */}
          <div className="p-3 rounded-xl bg-emerald-50/70 border border-emerald-200 flex items-start gap-3">
            <div className="p-2 rounded-lg bg-emerald-100 text-emerald-800 shrink-0">
              <Award className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <MetricHelpTooltip
                helpKey="bestMargin"
                label="Best Margin"
                className="text-[10px] uppercase font-bold text-emerald-800"
                iconClassName="w-2.5 h-2.5 text-emerald-700/70"
              />
              <div className="text-base font-bold font-mono text-emerald-950 mt-0.5">
                {summary.completedPerformance.bestMarginCycle
                  ? `${summary.completedPerformance.bestMarginCycle.grossMarginPct}%`
                  : '—'}
              </div>
              <p className="text-[11px] text-emerald-800/80 truncate">
                {summary.completedPerformance.bestMarginCycle
                  ? `${summary.completedPerformance.bestMarginCycle.cycleCode} (${summary.completedPerformance.bestMarginCycle.growerName})`
                  : 'No completed cycles'}
              </p>
            </div>
          </div>

          {/* Lowest Margin */}
          <div className="p-3 rounded-xl bg-amber-50/70 border border-amber-200 flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-100 text-amber-800 shrink-0">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <MetricHelpTooltip
                helpKey="lowestMargin"
                label="Lowest Margin"
                className="text-[10px] uppercase font-bold text-amber-800"
                iconClassName="w-2.5 h-2.5 text-amber-700/70"
              />
              <div className="text-base font-bold font-mono text-amber-950 mt-0.5">
                {summary.completedPerformance.lowestMarginCycle
                  ? `${summary.completedPerformance.lowestMarginCycle.grossMarginPct}%`
                  : '—'}
              </div>
              <p className="text-[11px] text-amber-800/80 truncate">
                {summary.completedPerformance.lowestMarginCycle
                  ? `${summary.completedPerformance.lowestMarginCycle.cycleCode} (${summary.completedPerformance.lowestMarginCycle.growerName})`
                  : 'No completed cycles'}
              </p>
            </div>
          </div>

          {/* Lowest Mortality */}
          <div className="p-3 rounded-xl bg-indigo-50/70 border border-indigo-200 flex items-start gap-3">
            <div className="p-2 rounded-lg bg-indigo-100 text-indigo-800 shrink-0">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <MetricHelpTooltip
                helpKey="lowestMortality"
                label="Lowest Mortality"
                className="text-[10px] uppercase font-bold text-indigo-800"
                iconClassName="w-2.5 h-2.5 text-indigo-700/70"
              />
              <div className="text-base font-bold font-mono text-indigo-950 mt-0.5">
                {summary.completedPerformance.lowestMortalityCycle
                  ? `${summary.completedPerformance.lowestMortalityCycle.mortalityRatePct}%`
                  : '—'}
              </div>
              <p className="text-[11px] text-indigo-800/80 truncate">
                {summary.completedPerformance.lowestMortalityCycle
                  ? `${summary.completedPerformance.lowestMortalityCycle.cycleCode} (${summary.completedPerformance.lowestMortalityCycle.placedHead} placed)`
                  : 'No source-backed data'}
              </p>
            </div>
          </div>
        </div>

        {/* Compact Management Table (Desktop md:block) */}
        {summary.completedPerformance.completedCycles.length === 0 ? (
          <div className="p-4 text-center text-xs text-slate-400 bg-slate-50 rounded-lg">
            No completed (settled/closed) cycles recorded in this reporting period.
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200 select-none">
                  <tr>
                    <th
                      onClick={() => handleSort('cycleCode')}
                      className="py-2.5 px-3 cursor-pointer hover:bg-slate-200/70 transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        <MetricHelpTooltip helpKey="tableCycleCode" label="Production Cycle" iconClassName="w-2.5 h-2.5 text-slate-400" />
                        <ArrowUpDown className="w-3 h-3 text-slate-400" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('growerName')}
                      className="py-2.5 px-3 cursor-pointer hover:bg-slate-200/70 transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        <MetricHelpTooltip helpKey="tableRaiserName" label="Raiser" iconClassName="w-2.5 h-2.5 text-slate-400" />
                        <ArrowUpDown className="w-3 h-3 text-slate-400" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('soldHead')}
                      className="py-2.5 px-3 text-right cursor-pointer hover:bg-slate-200/70 transition-colors"
                    >
                      <div className="flex items-center justify-end gap-1">
                        <MetricHelpTooltip helpKey="tableSoldHead" label="Pigs Sold" iconClassName="w-2.5 h-2.5 text-slate-400" />
                        <ArrowUpDown className="w-3 h-3 text-slate-400" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('totalRevenue')}
                      className="py-2.5 px-3 text-right cursor-pointer hover:bg-slate-200/70 transition-colors"
                    >
                      <div className="flex items-center justify-end gap-1">
                        <MetricHelpTooltip helpKey="tableTotalRevenue" label="Sales Revenue" iconClassName="w-2.5 h-2.5 text-slate-400" />
                        <ArrowUpDown className="w-3 h-3 text-slate-400" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('grossProfit')}
                      className="py-2.5 px-3 text-right cursor-pointer hover:bg-slate-200/70 transition-colors"
                    >
                      <div className="flex items-center justify-end gap-1">
                        <MetricHelpTooltip helpKey="tableGrossProfit" label="Gross Profit" iconClassName="w-2.5 h-2.5 text-slate-400" />
                        <ArrowUpDown className="w-3 h-3 text-slate-400" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('grossMarginPct')}
                      className="py-2.5 px-3 text-right cursor-pointer hover:bg-slate-200/70 transition-colors"
                    >
                      <div className="flex items-center justify-end gap-1">
                        <MetricHelpTooltip helpKey="tableGrossMarginPct" label="Margin %" iconClassName="w-2.5 h-2.5 text-slate-400" />
                        <ArrowUpDown className="w-3 h-3 text-slate-400" />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-mono">
                  {visibleCompletedCycles.map((c) => (
                    <tr key={c.cycleId} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-2 px-3 font-semibold text-slate-900">{c.cycleCode}</td>
                      <td className="py-2 px-3 font-sans text-slate-600 truncate max-w-[150px]">{c.growerName}</td>
                      <td className="py-2 px-3 text-right text-slate-700">{formatNumber(c.soldHead)}</td>
                      <td className="py-2 px-3 text-right text-slate-900 font-semibold">{formatCurrency(c.totalRevenue)}</td>
                      <td className={`py-2 px-3 text-right font-bold ${c.grossProfit >= 0 ? 'text-emerald-800' : 'text-rose-700'}`}>
                        {formatCurrency(c.grossProfit)}
                      </td>
                      <td className={`py-2 px-3 text-right font-bold ${c.grossMarginPct !== null && c.grossMarginPct >= 20 ? 'text-emerald-800' : 'text-slate-800'}`}>
                        {c.grossMarginPct !== null ? `${c.grossMarginPct}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Touch Cards (< 768px) */}
            <div className="md:hidden space-y-2">
              {visibleCompletedCycles.map((c) => (
                <div key={c.cycleId} className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900 font-mono">{c.cycleCode}</span>
                    <span className={`font-mono font-bold ${c.grossMarginPct !== null && c.grossMarginPct >= 20 ? 'text-emerald-800' : 'text-slate-800'}`}>
                      {c.grossMarginPct !== null ? `${c.grossMarginPct}% Margin` : '—'}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500">{c.growerName}</div>
                  <div className="grid grid-cols-3 gap-1 pt-1.5 border-t border-slate-200/60 font-mono text-[11px]">
                    <div>
                      <span className="text-[9px] text-slate-400 uppercase block font-sans">Sold</span>
                      <span>{formatNumber(c.soldHead)} hd</span>
                    </div>
                    <div>
                      <span className="text-[9px] text-slate-400 uppercase block font-sans">Revenue</span>
                      <span className="font-semibold text-slate-900">{formatCurrency(c.totalRevenue)}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[9px] text-slate-400 uppercase block font-sans">Profit</span>
                      <span className={`font-bold ${c.grossProfit >= 0 ? 'text-emerald-800' : 'text-rose-700'}`}>
                        {formatCurrency(c.grossProfit)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Progressive Disclosure Button */}
            {sortedCompletedCycles.length > 5 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAllCompleted(!showAllCompleted)}
                className="w-full text-xs font-semibold text-slate-700 h-10 min-h-[44px] flex items-center justify-center gap-1.5"
              >
                <span>{showAllCompleted ? 'Show Top 5 Completed Cycles' : `View All ${sortedCompletedCycles.length} Completed Cycles`}</span>
                {showAllCompleted ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </Button>
            )}
          </>
        )}
      </div>

      {/* =========================================================
          5. PRODUCTION COST MIX & MOVEMENT
          ========================================================= */}
      <div className="space-y-3 bg-white p-4 sm:p-5 border border-slate-200 rounded-xl shadow-xs">
        <div className="flex items-center justify-between pb-2 border-b border-slate-100">
          <div className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider text-slate-800">
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-slate-800 text-white text-[10px] font-bold">5</span>
            <span>Production Cost Mix & Movement</span>
          </div>
          <span className="text-xs font-semibold text-slate-500 font-mono">
            {formatCurrency(summary.totalBookedProductionCosts)} Total Direct
          </span>
        </div>

        {/* 4 Cost Mix Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
          {summary.costMix.map((cat) => (
            <div key={cat.category} className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2">
              <div className="flex items-center justify-between">
                <MetricHelpTooltip
                  helpKey={costMixHelpMap[cat.category]}
                  label={cat.label}
                  className="text-xs font-bold text-slate-900 truncate"
                />
                <span className="text-[10px] font-mono text-slate-400 shrink-0">{cat.accountCodes}</span>
              </div>

              <div>
                <div className="text-sm sm:text-base font-bold font-mono text-slate-900">
                  <MetricHelpTooltip
                    helpKey="costMetricAmount"
                    label={formatCurrency(cat.amount)}
                    className="text-sm sm:text-base font-bold font-mono text-slate-900"
                    showIcon={false}
                  />
                </div>
                <div className="flex items-center justify-between mt-1 text-[11px]">
                  <MetricHelpTooltip
                    helpKey="costMetricPct"
                    label={`${cat.pctOfTotalCosts}% of costs`}
                    className="font-semibold text-slate-600"
                    showIcon={false}
                  />
                  {cat.changePct !== null ? (
                    <MetricHelpTooltip
                      helpKey="costMetricYoY"
                      label={`${cat.changePct >= 0 ? '+' : ''}${cat.changePct}% vs prior`}
                      className={`font-bold ${cat.changePct <= 0 ? 'text-emerald-700' : 'text-rose-700'}`}
                      showIcon={false}
                    />
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </div>
              </div>

              {/* Progress bar representing share */}
              <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-emerald-700 h-full rounded-full"
                  style={{ width: `${Math.min(100, Math.max(0, cat.pctOfTotalCosts))}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* =========================================================
          6. MANAGEMENT ATTENTION
          ========================================================= */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider text-slate-800">
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-slate-800 text-white text-[10px] font-bold">6</span>
            <span>Management Attention</span>
          </div>
          <span className="text-[10px] font-mono text-slate-400">Triage & Administrative Watchlist</span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
          {/* Needs Review */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="attentionNeedsReview"
                  label="Needs Review"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0" />
              </div>
              <div>
                <div className={`text-sm sm:text-lg font-bold font-mono tracking-tight ${summary.managementAttention.needsReviewCount > 0 ? 'text-rose-700' : 'text-slate-900'}`}>
                  {summary.managementAttention.needsReviewCount}
                </div>
                <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5 truncate">
                  Open Triage Queue Issues
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Incomplete Records */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="attentionIncompleteRecords"
                  label="Incomplete Records"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <FileWarning className="w-4 h-4 text-amber-600 shrink-0" />
              </div>
              <div>
                <div className={`text-sm sm:text-lg font-bold font-mono tracking-tight ${summary.managementAttention.incompleteRecordsCount > 0 ? 'text-amber-800' : 'text-slate-900'}`}>
                  {summary.managementAttention.incompleteRecordsCount}
                </div>
                <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5 truncate">
                  Missing Placed Head/Dates
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Outstanding Settlements */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="attentionOutstandingSettlements"
                  label="Unsettled Cycles"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <Scale className="w-4 h-4 text-indigo-600 shrink-0" />
              </div>
              <div>
                <div className="text-sm sm:text-lg font-bold font-mono text-indigo-900 tracking-tight">
                  {summary.managementAttention.outstandingSettlementsCount}
                </div>
                <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5 truncate">
                  Cycles with Sales Unfrozen
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Unresolved Cycle Issues */}
          <Card className="border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-all">
            <CardContent className="p-3 sm:p-3.5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between text-slate-500 mb-1">
                <MetricHelpTooltip
                  helpKey="attentionCycleIssues"
                  label="Cycle-Linked Issues"
                  className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate text-slate-500"
                />
                <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0" />
              </div>
              <div>
                <div className={`text-sm sm:text-lg font-bold font-mono tracking-tight ${summary.managementAttention.unresolvedCycleIssuesCount > 0 ? 'text-amber-800' : 'text-slate-900'}`}>
                  {summary.managementAttention.unresolvedCycleIssuesCount}
                </div>
                <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5 truncate">
                  Queue Items Attached to Cycles
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
